import { askConfirm } from '@/components/ui/ConfirmHost';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postOfferAccept, postOfferDecline } from '@/api/applicant';
import { errMsg } from '@/components/kit/data';
import { fmtDate, naira } from '@/lib/format';
import { appKeys, useStatus } from '../shared';
import { AdmissionCard } from './AdmissionCard';
import { Actions, Body, Hero, Loading, Panel, Pill, Primary, Rows, Screen, Secondary } from '../ui';

/** Admission: the letter once admitted, or the offer / waitlist / not-admitted states. */
export function OfferPage() {
  const qc = useQueryClient();
  const q = useStatus();
  const [msg, setMsg] = useState<string | null>(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: appKeys.status }); qc.invalidateQueries({ queryKey: appKeys.app }); };
  const accept = useMutation({ mutationFn: postOfferAccept, onSuccess: refresh, onError: (e) => setMsg(errMsg(e)) });
  const decline = useMutation({ mutationFn: postOfferDecline, onSuccess: refresh, onError: (e) => setMsg(errMsg(e)) });

  const s = q.data;
  const d = s?.decision ?? null;
  const first = s?.name?.split(' ')[0];
  if (q.isLoading) return <Screen><Hero title="Admission" /><Loading /></Screen>;

  if (s?.admission) {
    return (
      <Screen>
        <Hero title="Congratulations" sub={s.admission.class?.name} />
        <Body><AdmissionCard admission={s.admission} name={s.name} /></Body>
      </Screen>
    );
  }

  const expired = !!d?.offer_expires_at && new Date(d.offer_expires_at).getTime() < Date.now();
  const open = d?.decision === 'admit' && d.offer_response === 'pending' && s?.status === 'admitted' && !expired;

  return (
    <Screen>
      <Hero title={d?.decision === 'admit' ? 'Accept your place' : 'Application update'} sub={d?.class?.name ?? s?.name} />
      <Body>
        {msg && <Panel tone="red"><p className="text-sm text-red-700">{msg}</p></Panel>}
        {!d ? (
          <Panel><p className="text-slate-600">No decision yet. We will notify you as soon as the school decides.</p></Panel>
        ) : d.decision === 'admit' ? (
          <>
            <Panel><p className="text-2xl font-extrabold text-[#0A2540]">Congratulations{first ? ` ${first}` : ''}</p><p className="text-slate-600 mt-1">You have been offered a place{d.class ? ` in ${d.class.name}` : ''}{s?.cycle?.name ? ` for ${s.cycle.name}` : ''}.</p></Panel>
            <Panel><Rows rows={[
              ...(s?.cycle?.acceptance_fee ? [['Acceptance fee', naira(s.cycle.acceptance_fee)] as [string, string]] : []),
              ...(d.offer_expires_at ? [['Offer valid until', fmtDate(d.offer_expires_at)] as [string, string]] : []),
              ['Your response', d.offer_response === 'pending' ? 'Waiting' : d.offer_response ?? '—'],
            ]} /></Panel>
            {expired && <Panel tone="red"><p className="text-sm text-red-700">This offer has expired. Please contact the school.</p></Panel>}
            {open && (
              <Actions>
                <Primary busy={accept.isPending} onClick={() => accept.mutate()}>Accept offer</Primary>
                <Secondary disabled={decline.isPending} onClick={async () => { if (await askConfirm('Decline this offer? This cannot be undone.', { title: 'Decline offer', confirmLabel: 'Decline' })) decline.mutate(); }}>Decline offer</Secondary>
              </Actions>
            )}
          </>
        ) : d.decision === 'waitlist' ? (
          <>
            <Panel><div className="flex items-center justify-between"><p className="text-xl font-bold">Status</p><Pill tone="amber">Waitlisted</Pill></div>
              <p className="text-slate-600 mt-2">{s?.class_applied?.name ?? 'The class'} is full for now. We will message you if a seat opens.</p>{d.note && <p className="text-slate-600 mt-1">{d.note}</p>}</Panel>
            <Panel><p className="text-xl font-bold">Other options</p><p className="text-slate-600 mt-1">Ask the school to look again, or apply for another class or session.</p></Panel>
            <Actions><Primary to="/applicant/notifications">Contact the school</Primary></Actions>
          </>
        ) : (
          <Panel><p className="text-xl font-bold">Not admitted this time</p><p className="text-slate-600 mt-1">{d.note ?? 'We are sorry. The school was unable to offer you a place this time.'}</p></Panel>
        )}
      </Body>
    </Screen>
  );
}
